import { useRef, useState } from 'react';
import { useDismiss } from '../lib/dismiss.ts';
import { useStore } from '../lib/store.ts';
import { app } from '../state/app.ts';
import { tasksStore } from '../state/tasks.ts';
import { openTab, setPaneCount, tabs, type TabKind } from '../state/tabs.ts';
import { Icon, JeromeMark, type IconName } from '../ui/Icon.tsx';
import { Badge, IconButton } from '../ui/primitives.tsx';
import { initials } from '../lib/format.ts';
import { useVoiceCapability, VOICE_OFF_TITLE } from '../voice/capability.ts';
import { showVoiceError, VoiceError } from '../voice/errors.ts';
import { RecentActions } from './RecentActions.tsx';

// Bovenbalk op de desktop: merk met versiepil (klik: wensen, fouten en changelog), functieknoppen
// (Mail met tellers) plus een knop per Excel-werkboek, rechts recente acties, paneelschakelaar
// (alleen op een breed scherm), werkerskolom, spraakmodus (gedempt zonder ElevenLabs-sleutel) en
// profielmenu. De telefoon heeft zijn eigen balk (PhoneTopBar).

const FUNCTIONS: Array<{ kind: TabKind; label: string; icon: IconName }> = [
  { kind: 'mail', label: 'Mail', icon: 'mail' },
  { kind: 'chat', label: 'Chat', icon: 'chat' },
  { kind: 'agenda', label: 'Agenda', icon: 'calendar' },
  { kind: 'rules', label: 'Regels', icon: 'sliders' },
  { kind: 'dev', label: 'Ontwikkelen', icon: 'code' },
];

export function TopBar(props: { railVisible: boolean; onToggleRail: () => void; onVoice: () => void; voiceActive: boolean }) {
  const me = useStore(app, (s) => s.me);
  const version = useStore(app, (s) => s.serverVersion ?? s.me?.version ?? null);
  const wsStatus = useStore(app, (s) => s.wsStatus);
  const newVersion = useStore(app, (s) => s.newVersion);
  const counts = useStore(tasksStore, (s) => s.counts);
  const paneCount = useStore(tabs, (s) => s.paneCount);
  const wide = useStore(app, (s) => s.wide);
  const voiceMuted = !useVoiceCapability().dictation;
  const activeTab = useStore(tabs, (s) => {
    const pane = s.panes[s.activePane];
    return pane.activeTabId ? s.tabs[pane.activeTabId] : undefined;
  });
  const activeKind = activeTab?.kind;
  const activeWorkbookId = activeTab?.kind === 'excel' ? (activeTab.state['workbookId'] as string | undefined) : undefined;
  const workbooks = useStore(app, (s) => s.settings.workbooks);

  return (
    <header className="h-14 shrink-0 flex items-center gap-2 px-3 bg-paper border-b border-line">
      <button type="button" className="flex items-center gap-2 rounded-md px-1 hover:bg-tint" onClick={() => openTab('mail', { single: true })} aria-label="Naar Mail">
        <JeromeMark size={30} />
        <span className="hidden sm:inline font-display font-bold text-[22px] tracking-tight text-ink">Jerome</span>
      </button>
      <button type="button" aria-label="Versie en wensen" title="Versie, wensen en fouten, changelog" onClick={() => openTab('releases', { single: true })} className="mr-2 hidden sm:inline-flex items-center h-6 px-2 rounded-full bg-tint text-[12px] font-medium tabular-nums text-ink-soft hover:bg-tint-2 hover:text-ink">
        {`v${version ?? '…'}`}
      </button>
      <nav className="flex items-center gap-1 min-w-0 overflow-x-auto scroll-hidden" aria-label="Functies">
        {FUNCTIONS.map((f) => {
          const active = activeKind === f.kind;
          const isMail = f.kind === 'mail';
          return (
            <button key={f.kind} type="button" onClick={() => openTab(f.kind, { single: f.kind !== 'chat' })} aria-current={active ? 'page' : undefined} title={f.label} className={`shrink-0 inline-flex items-center gap-2 h-10 rounded-full px-2.5 md:px-3.5 text-[15px] font-medium transition-colors ${active ? 'bg-bar text-white' : 'text-ink-soft hover:bg-tint hover:text-ink'}`}>
              <Icon name={f.icon} size={18} />
              <span className="sr-only md:not-sr-only">{f.label}</span>
              {isMail && counts.done > 0 ? <Badge tone="blue" title="Resultaten klaar">{counts.done}</Badge> : null}
              {isMail && counts.question > 0 ? <Badge tone="amber" title="Vragen aan jou">{counts.question}</Badge> : null}
            </button>
          );
        })}
        {workbooks.map((wb) => {
          const active = activeKind === 'excel' && (activeWorkbookId ?? workbooks[0]?.id) === wb.id;
          return (
            <button key={`wb-${wb.id}`} type="button" title={`Excel: ${wb.name}`} onClick={() => openTab('excel', { single: true, title: wb.name, state: { workbookId: wb.id } })} aria-current={active ? 'page' : undefined} className={`shrink-0 inline-flex items-center gap-2 h-10 rounded-full px-2.5 md:px-3.5 text-[15px] font-medium whitespace-nowrap transition-colors ${active ? 'bg-bar text-white' : 'text-ink-soft hover:bg-tint hover:text-ink'}`}>
              <Icon name="table" size={18} />
              <span className="sr-only md:not-sr-only">{wb.name}</span>
            </button>
          );
        })}
      </nav>
      <div className="ml-auto shrink-0 flex items-center gap-2">
        {newVersion ? (
          <button type="button" className="hidden md:inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-amber-soft text-amber text-[13px] font-medium" onClick={() => location.reload()}>
            <Icon name="refresh" size={14} /> Nieuwe versie, herlaad
          </button>
        ) : null}
        {wsStatus !== 'open' ? <span className="hidden md:inline text-[12px] text-muted" title="Verbinding met Jerome">{wsStatus === 'verbinden' ? 'Verbinden…' : 'Geen verbinding'}</span> : null}
        <RecentActionsButton />
        {wide ? (
          <div className="flex items-center rounded-lg bg-tint p-0.5" role="group" aria-label="Panelen">
            <IconButton icon="panes1" label="Eén paneel" active={paneCount === 1} onClick={() => setPaneCount(1)} className="h-8 w-9" />
            <IconButton icon="panes2" label="Twee panelen" active={paneCount === 2} onClick={() => setPaneCount(2)} className="h-8 w-9" />
          </div>
        ) : null}
        <IconButton icon="rail" label={wide ? (props.railVisible ? 'Werkers inklappen' : 'Werkers uitklappen') : props.railVisible ? 'Werkers verbergen' : 'Werkers tonen'} active={props.railVisible} onClick={props.onToggleRail} />
        <IconButton icon="mic" label="Spraakmodus (v)" tone="amber" active={props.voiceActive} aria-disabled={voiceMuted || undefined} onClick={voiceMuted ? () => showVoiceError(new VoiceError('off'), 'dictation') : props.onVoice} className={`rounded-full h-10 w-10 ${voiceMuted ? 'opacity-50' : ''}`} size={20} {...(voiceMuted ? { title: VOICE_OFF_TITLE } : {})} />
        <ProfileMenu name={me?.name ?? ''} />
      </div>
    </header>
  );
}

function RecentActionsButton() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(ref, () => setOpen(false), { enabled: open });
  return (
    <div ref={ref} className="relative">
      <IconButton icon="clock" label="Recente acties" active={open} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((o) => !o)} />
      {open ? (
        <div role="dialog" aria-label="Recente acties" className="absolute right-0 mt-1 w-[380px] max-h-[70vh] flex flex-col rounded-lg bg-surface border border-line shadow-card z-30 overflow-hidden">
          <RecentActions onClose={() => setOpen(false)} />
        </div>
      ) : null}
    </div>
  );
}

function ProfileMenu(props: { name: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(ref, () => setOpen(false), { enabled: open });
  const items: Array<{ kind: TabKind; label: string; icon: IconName }> = [
    { kind: 'settings', label: 'Instellingen', icon: 'settings' },
    { kind: 'knowledge', label: 'Kennis', icon: 'book' },
    { kind: 'usage', label: 'Gebruik', icon: 'chart' },
    { kind: 'briefing', label: 'Samenvattingen', icon: 'sun' },
  ];
  return (
    <div ref={ref} className="relative">
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label="Profielmenu" onClick={() => setOpen((o) => !o)} className="h-10 w-10 rounded-full bg-tint text-ink font-semibold text-sm flex items-center justify-center hover:bg-tint-2" title={props.name}>
        {initials(props.name) || <Icon name="user" size={18} />}
      </button>
      {open ? (
        <div role="menu" className="absolute right-0 mt-1 w-52 rounded-lg bg-surface border border-line shadow-card py-1 z-30">
          <div className="px-3 py-2 text-[13px] text-muted border-b border-line">{props.name}</div>
          {items.map((it) => (
            <button key={it.kind} role="menuitem" type="button" className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-ink hover:bg-tint" onClick={() => { openTab(it.kind, { single: true }); setOpen(false); }}>
              <Icon name={it.icon} size={16} className="text-ink-soft" /> {it.label}
            </button>
          ))}
          <div className="border-t border-line mt-1 pt-1">
            <button role="menuitem" type="button" className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-ink hover:bg-tint" onClick={() => void fetch('/auth/logout', { method: 'POST' }).then(() => location.reload())}>
              <Icon name="logout" size={16} className="text-ink-soft" /> Uitloggen
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
